import React, { useState, useEffect } from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { format } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Edit, Trash2, Copy, RefreshCw, AlertTriangle, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Contract } from "@/lib/types";
import { useNavigate } from "react-router-dom";
import { importanceBadges, BadgeVariant } from "./types";
import { ExpirySticker } from "./ExpirySticker";
import { TypeTooltip } from "./TypeTooltip";
import { CategoryTooltip } from "./CategoryTooltip";
import { useCurrencies } from "@/hooks/useCurrencies";
import { toast } from "sonner";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { currencyConverter } from "@/services/currencyConverter";
import { useTranslation } from "react-i18next";
import { useImportanceColors } from "@/hooks/useImportanceColors";
import { GroupBadge } from "../group/GroupBadge";
import { CostComparisonIndicator } from "./CostComparisonIndicator";
import { DirectionText, getDirectionIcon } from "@/components/contract-types/ContractDirectionIcons";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { FeatureGuard } from "@/components/common/FeatureGuard";

interface ContractTableRowProps {
  contract: Contract;
  index: number;
  isColumnSelected: (id: string) => boolean;
  selectedContracts: Set<string>;
  toggleContractSelection: (id: string) => void;
  handleDeleteContract: (id: string) => Promise<void>;
  isPartOfGroup?: boolean;
}

export function ContractTableRow({
  contract,
  index,
  isColumnSelected,
  selectedContracts,
  toggleContractSelection,
  handleDeleteContract,
  isPartOfGroup = false
}: ContractTableRowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { useCurrencyQuery } = useCurrencies();
  const { useUserPreferencesQuery } = useUserPreferences();
  const { useHasRoleQuery } = useUserRoles();
  const { getImportanceColor } = useImportanceColors();
  const { canUseCurrencyConversion, canUsePriceComparison } = useFeatureAccess();
  
  const { data: userPreferences } = useUserPreferencesQuery();
  
  const { data: isDeveloper } = useHasRoleQuery("DEVELOPER");
  const { data: isAdmin } = useHasRoleQuery("ADMIN");
  const hasDebugAccess = isDeveloper || isAdmin;
  
  const { 
    data: currencyData,
    isLoading: isCurrencyLoading
  } = useCurrencyQuery(contract.currencyId || '');
  
  const { 
    data: defaultCurrencyData,
    isLoading: isDefaultCurrencyLoading
  } = useCurrencyQuery(userPreferences?.defaultCurrencyId || '');
  
  const [convertedAmount, setConvertedAmount] = React.useState<number | null>(null);
  const [conversionRate, setConversionRate] = React.useState<number>(1);
  const [isConverting, setIsConverting] = React.useState(false);
  
  React.useEffect(() => {
    if (hasDebugAccess && contract.currencyId) {
      console.log(`Contract ${contract.id} currency ID: ${contract.currencyId}`);
      console.log(`Currency data loaded:`, currencyData);
    }
  }, [contract.currencyId, currencyData, contract.id, hasDebugAccess]);
  
  React.useEffect(() => {
    const convertCurrency = async () => {
      if (
        canUseCurrencyConversion && // Check feature access instead of system setting
        currencyData && 
        defaultCurrencyData && 
        currencyData.id !== defaultCurrencyData.id &&
        contract.currencyId && 
        userPreferences?.defaultCurrencyId
      ) {
        setIsConverting(true);
        try {
          const result = await currencyConverter.convert(
            contract.expectedCost,
            contract.currencyId,
            userPreferences.defaultCurrencyId
          );
          
          setConvertedAmount(result.convertedAmount);
          setConversionRate(result.rate);
        } catch (error) {
          console.error("Error converting currency:", error);
          setConvertedAmount(null);
        } finally {
          setIsConverting(false);
        }
      } else {
        setConvertedAmount(null);
      }
    };
    
    convertCurrency();
  }, [currencyData, defaultCurrencyData, contract.expectedCost, contract.currencyId, userPreferences?.defaultCurrencyId, canUseCurrencyConversion]);
  
  const getCurrencySymbol = () => {
    if (currencyData) {
      return currencyData.symbol;
    } else if (contract.currency) {
      return contract.currency.symbol;
    }
    return '';
  };
  
  const getCurrencyCode = () => {
    if (currencyData) {
      return currencyData.code;
    } else if (contract.currency) {
      return contract.currency.code;
    }
    return '';
  };
  
  const currencySymbol = getCurrencySymbol();
  const currencyCode = getCurrencyCode();

  const copyContractToClipboard = () => {
    const contractData = JSON.stringify(contract, null, 2);
    navigator.clipboard.writeText(contractData)
      .then(() => {
        toast.success("Contract data copied to clipboard");
      })
      .catch(err => {
        toast.error("Failed to copy contract data");
        console.error("Copy failed:", err);
      });
  };

  const isIncome = contract.type?.direction === "incoming";

  return (
    <TableRow 
      key={contract.id} 
      className={`
        group transition-colors hover:bg-muted/60 dark:hover:bg-gray-900/70
        ${index % 2 === 0 ? 'bg-white dark:bg-gray-950' : 'bg-muted/20 dark:bg-gray-900/20'}
        dark:border-gray-800
        ${isPartOfGroup ? 'border-l-2 border-l-blue-500 dark:border-l-blue-600' : ''}
      `}
    >
      {isColumnSelected("checkbox") && (
        <TableCell className="w-12 border-r dark:border-gray-800">
          <Checkbox
            checked={selectedContracts.has(contract.id)}
            onCheckedChange={() => toggleContractSelection(contract.id)}
            aria-label={`Select ${contract.supplierName}`}
            className="dark:border-blue-500 dark:text-blue-500"
          />
        </TableCell>
      )}
      
      {isColumnSelected("supplierName") && (
        <TableCell className="font-medium border-r dark:border-gray-800 dark:text-white">
          {contract.supplierName}
        </TableCell>
      )}
      
      {isColumnSelected("importance") && (
        <TableCell className="border-r dark:border-gray-800">
          {(() => {
            const { className, style } = getImportanceColor(contract.importance);
            
            return (
              <Badge 
                variant={importanceBadges[contract.importance].variant as BadgeVariant}
                className={className}
                style={style}
              >
                {importanceBadges[contract.importance].label}
              </Badge>
            );
          })()}
        </TableCell>
      )}
      
      {isColumnSelected("expiryStatus") && (
        <TableCell className="border-r dark:border-gray-800">
          <ExpirySticker contract={contract} />
        </TableCell>
      )}
      
      {isColumnSelected("type") && (
        <TableCell className="border-r dark:border-gray-800 dark:text-gray-300">
          {contract.type && (
            <div className="flex items-center gap-1.5">
              {contract.type.direction && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      {getDirectionIcon(contract.type.direction)}
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <div className="text-sm"><DirectionText direction={contract.type.direction} /></div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <TypeTooltip type={contract.type} />
            </div>
          )}
        </TableCell>
      )}
      
      {isColumnSelected("category") && (
        <TableCell className="border-r dark:border-gray-800 dark:text-gray-300">
          <CategoryTooltip category={contract.category} />
        </TableCell>
      )}
      
      {isColumnSelected("expiryDate") && (
        <TableCell className="border-r dark:border-gray-800 dark:text-gray-300">
          {format(contract.expiryDate, "MMM d, yyyy")}
        </TableCell>
      )}
      
      {isColumnSelected("signedDate") && (
        <TableCell className="border-r dark:border-gray-800 dark:text-gray-300">
          {format(contract.signedDate, "MMM d, yyyy")}
        </TableCell>
      )}
      
      {isColumnSelected("expectedCost") && (
        <TableCell className="border-r dark:border-gray-800 dark:text-gray-300">
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              {isIncome ? (
                <ArrowUpRight className="h-4 w-4 text-green-500" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-red-500" />
              )}
              <span className={isIncome ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                {contract.expectedCost.toLocaleString()}
                {currencySymbol && ` ${currencySymbol}`}
              </span>
              
              {contract.typeId && (
                <FeatureGuard feature="price_comparison" inline>
                  <CostComparisonIndicator
                    contractCost={contract.expectedCost}
                    supplierName={contract.supplierName}
                    organizationNumber={contract.organisationnumber || null}
                    contractTypeId={contract.typeId}
                  />
                </FeatureGuard>
              )}
            </div>
            
            {convertedAmount !== null && defaultCurrencyData && (
              <FeatureGuard feature="currency_conversion" inline>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1 text-xs mt-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900 rounded-full cursor-help max-w-fit">
                        <RefreshCw className="h-3 w-3 text-blue-500" />
                        <span className="text-blue-700 dark:text-blue-300">
                          {convertedAmount.toLocaleString(undefined, {maximumFractionDigits: 2})} 
                          {defaultCurrencyData.symbol}
                        </span>
                        <AlertTriangle className="h-3 w-3 text-amber-500 ml-0.5 opacity-70" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-black text-white border-gray-800 p-3 max-w-[300px]">
                      <div className="space-y-2">
                        <p>Converted at rate: 1 {currencyCode} = {conversionRate.toFixed(4)} {defaultCurrencyData.code}</p>
                        <p className="text-amber-300 text-xs flex items-start gap-1.5">
                          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <span>{t('currencyDisclaimer', 'Currency conversions are estimates and may not reflect exact rates at the time of contract payment.')}</span>
                        </p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </FeatureGuard>
            )}
            
            {isConverting && canUseCurrencyConversion && (
              <span className="ml-1 text-xs text-gray-400">converting...</span>
            )}
          </div>
        </TableCell>
      )}
      
      {isColumnSelected("currency") && (
        <TableCell className="border-r dark:border-gray-800 dark:text-gray-300">
          {isCurrencyLoading ? (
            <span className="text-xs text-gray-400">Loading...</span>
          ) : currencyCode ? (
            currencyCode
          ) : contract.currencyId ? (
            <span className="text-xs text-amber-600">ID: {contract.currencyId.substring(0, 8)}...</span>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </TableCell>
      )}
      
      {isColumnSelected("group") && (
        <TableCell className="border-r dark:border-gray-800 min-w-[120px]">
          {contract.group ? (
            <GroupBadge 
              groupName={contract.group.name} 
              groupDescription={contract.group.description} 
            />
          ) : (
            <span className="text-muted-foreground text-sm">Ungrouped</span>
          )}
        </TableCell>
      )}
      
      {isColumnSelected("actions") && (
        <TableCell className="text-right border-r dark:border-gray-800 w-[120px]">
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/contracts/${contract.id}`)}
              className="h-8 w-8"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/contracts/${contract.id}/edit`)}
              className="h-8 w-8"
            >
              <Edit className="h-4 w-4" />
            </Button>
            
            {hasDebugAccess && (
              <Button
                variant="ghost"
                size="icon"
                onClick={copyContractToClipboard}
                className="h-8 w-8"
                title="Copy contract data to clipboard"
              >
                <Copy className="h-4 w-4" />
              </Button>
            )}
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete contract?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete the contract
                    and all associated data.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={() => handleDeleteContract(contract.id)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}
